import { Button, ButtonGroup, Checkbox, Radio, RadioGroup, Stack, Text, Textarea } from '@chakra-ui/react';
import React, { ReactElement, useState } from 'react';
import { ApplicantStatusReason, IApplyUniConfig } from 'types';
import { CustomFormControl, Modal, Select } from 'shared-components';
import { statusReasonOptions } from 'data';
import { useSelector } from 'react-redux';
import { ApplicationState } from '@/store';

type SubFlow = 'reRequest' | 'withdraw';

interface RequirementNotSatisfiedModalProps {
  isOpen: boolean;
  onClose: () => void;
  onReRequest: (description: string, internalNote: string) => void;
  onWithdraw: (reason: ApplicantStatusReason, explanation: string, note: string, notify: boolean) => void;
  defaultDescription?: string;
}

export const RequirementNotSatisfiedModal = ({
  isOpen,
  onClose,
  onReRequest,
  onWithdraw,
  defaultDescription = '',
}: RequirementNotSatisfiedModalProps): ReactElement => {
  const [subFlow, setSubFlow] = useState<SubFlow>('reRequest');
  const [description, setDescription] = useState(defaultDescription);
  const [internalNote, setInternalNote] = useState('');
  const [statusReason, setStatusReason] = useState<ApplicantStatusReason | ''>('');
  const [withdrawNote, setWithdrawNote] = useState('');
  const [withdrawExplanationToApplicant, setWithdrawExplanationToApplicant] = useState('');
  const [notifyApplicant, setNotifyApplicant] = useState(false);
  const { applyConfig } = useSelector((state: ApplicationState) => state.applyConfig);

  const getDefaultExplanationToApplicant = (reason: ApplicantStatusReason | ''): string => {
    if (!reason) {
      return '';
    }

    const reasonType = (reason.charAt(0).toLowerCase() + reason.slice(1)) as keyof NonNullable<
      IApplyUniConfig['rejectionReasons']
    >;

    return applyConfig.rejectionReasons?.[reasonType] ?? '';
  };

  const handleConfirm = (): void => {
    if (subFlow === 'reRequest') {
      onReRequest(description, internalNote);
    } else if (subFlow === 'withdraw' && statusReason) {
      onWithdraw(statusReason, withdrawExplanationToApplicant, withdrawNote, notifyApplicant);
    }
    resetState();
  };

  const resetState = (): void => {
    setSubFlow('reRequest');
    setDescription(defaultDescription);
    setInternalNote('');
    setStatusReason('');
    setWithdrawNote('');
    setWithdrawExplanationToApplicant('');
    setNotifyApplicant(false);
  };

  const handleClose = (): void => {
    resetState();
    onClose();
  };

  const isConfirmDisabled = subFlow === 'withdraw' && (!statusReason || String(statusReason) === '');

  const confirmLabel = subFlow === 'reRequest' ? 'Re-request task' : 'Withdraw offer';
  const confirmColorScheme = subFlow === 'reRequest' ? 'blue' : 'red';

  return (
    <Modal
      show={isOpen}
      onClose={handleClose}
      title="Requirement not satisfied"
      footer={
        <ButtonGroup spacing={3} size="lg">
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant="solid"
            colorScheme={confirmColorScheme}
            isDisabled={isConfirmDisabled}
            onClick={handleConfirm}
          >
            {confirmLabel}
          </Button>
        </ButtonGroup>
      }
    >
      <Stack spacing={4}>
        <Text fontWeight="medium" mb={0}>
          How do you want to proceed?
        </Text>

        <RadioGroup value={subFlow} onChange={(val: string) => setSubFlow(val as SubFlow)}>
          <Stack spacing={2}>
            <Radio value="reRequest" variant="outline">
              Re-request task
            </Radio>
            <Radio value="withdraw" variant="outline">
              Withdraw the entire offer
            </Radio>
          </Stack>
        </RadioGroup>

        {subFlow === 'reRequest' && (
          <Stack spacing={4}>
            <CustomFormControl label="Description" subLabel="Shared with the applicant" size="lg">
              <Textarea size="lg" placeholder="" value={description} onChange={e => setDescription(e.target.value)} />
            </CustomFormControl>

            <CustomFormControl
              label="Add an internal note (optional)"
              subLabel="Not shared with the applicant"
              size="lg"
            >
              <Textarea
                size="lg"
                placeholder="Add note..."
                value={internalNote}
                onChange={e => setInternalNote(e.target.value)}
              />
            </CustomFormControl>
          </Stack>
        )}

        {subFlow === 'withdraw' && (
          <Stack spacing={4}>
            <Select
              id="withdraw-reason"
              key="withdraw-reason"
              options={statusReasonOptions}
              value={statusReason}
              placeHolder="Choose reason"
              label="Reason"
              onChange={(evt: React.FormEvent<HTMLSelectElement>) => {
                const reason = evt.currentTarget.value as ApplicantStatusReason;
                setStatusReason(reason);
                setWithdrawExplanationToApplicant(getDefaultExplanationToApplicant(reason));
              }}
              size="lg"
            />

            <CustomFormControl
              id="withdraw-explanation-to-applicant"
              label="Explanation to applicant"
              labelProps={{ htmlFor: 'withdraw-explanation-to-applicant' }}
              size="lg"
            >
              <Textarea
                id="withdraw-explanation-to-applicant"
                size="lg"
                placeholder=""
                value={withdrawExplanationToApplicant}
                onChange={e => setWithdrawExplanationToApplicant(e.target.value)}
                data-selector="withdraw-explanation-to-applicant-textarea"
              />
            </CustomFormControl>

            <CustomFormControl
              id="withdraw-note"
              label="Internal note"
              labelProps={{ htmlFor: 'withdraw-note' }}
              subLabel="This will never be shown to the applicant"
              size="lg"
            >
              <Textarea
                id="withdraw-note"
                size="lg"
                placeholder="Add note..."
                value={withdrawNote}
                onChange={e => setWithdrawNote(e.target.value)}
              />
            </CustomFormControl>

            <Checkbox isChecked={notifyApplicant} onChange={e => setNotifyApplicant(e.target.checked)} size="lg">
              Notify applicant by email
            </Checkbox>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
};
